import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    MenuItem,
    TextField,
    Typography,
} from '@material-ui/core';
import AutoComplete from '@material-ui/lab/Autocomplete';
import NumberInput from '../../../../../common/components/UI/Input/Number';
import { UPDATE_VALIDATION_RULE } from '../../../store/ruleSet';
import { JobTransferValidationType, getJobTransferValidationTypeLabel, RequestTypes } from '../../../../Enums';
import StyledSelect from '../StyledSelect';
import InputTitle from '../InputTitle';
import StyledSwitch from '../StyledSwitch';
import OnRestrictionSection from './OnRestrictionSection';
import OnProbationSection from './OnProbationSection';
import './Step2-EmployeeRulesSection.scss';

/**
 * @param {{ validationErrors: {[Key: string], isDisabled: boolean }, stepNumber: number }} props
 */
export default function EmployeeRulesSection(props) {
    const { validationErrors, isDisabled, stepNumber, } = props;
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'settings.ruleSet' });
    const dispatch = useDispatch();
    /** @type {EmploymentBillboard.AdminStore} */
    // @ts-ignore
    const { adminRuleSet, adminAppData } = useSelector((state) => state);
    const { requestType, validationRuleSet } = adminRuleSet;
    const { jobTransferValidationType, isSkills, isCertifications, isEmployeeEligibility, employeeEligibilityLength,
        employeeEligibilityPeriod, employeeEligibilityDataviewId, employeeEligibilityDataviewKeyAlias,
    } = validationRuleSet;
    const { dataviews, symbolicPeriods } = adminAppData;
    const isVto = requestType === RequestTypes.voluntaryTimeOff;
    
    /**
     * @typedef {{value: JobTransferValidationType|null, label: string}} SelectOption
     * @type {SelectOption[]}
     */
    const jobTransferTypeOptions = [
        { value: null, label: t('employeeRulesStep.none') }, 
        ...Object.values(JobTransferValidationType).map((x) => ({ value: x, label: getJobTransferValidationTypeLabel(x) }))];

    /**
    * @typedef {{value: string, label: string, disabled: boolean}} SelectDataviewFieldOption
    * @type {SelectDataviewFieldOption[]}
    */
    const eligibilityDataviewFields = dataviews.find((x) => x.id === employeeEligibilityDataviewId)?.fields.map((x) => ({
        value: x.key,
        label: x.label,
    })) || [];

    /**
     * @param {string} target
     * @param {string} value
     */
    const updateValidationRulesStore = (target, value) => {
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                [target]: value,
            },
        });
    };

    const selectedJobTransferType = jobTransferTypeOptions.find((x) => x.value === jobTransferValidationType);
    const selectedEmployeeEligibilityPeriod = symbolicPeriods.find((x) => x.id === employeeEligibilityPeriod);
    const selectedEligibilityDataviewKeyAlias = eligibilityDataviewFields.find((x) => x.value === employeeEligibilityDataviewKeyAlias);

    return (
        <Card className="card">
            <CardHeader
                className="card--header"
                gutterbottom
                avatar={<Avatar className="avatar">{stepNumber}</Avatar>}
                title="Employee rules"
            />
            <CardContent className="ers-cardContent no-gap">
                {!isVto && (
                    <>
                        <InputTitle>Qualification</InputTitle>
                        <StyledSelect
                            labelId="select-jobTransfer-label"
                            id="select-jobTransfer-type"
                            disabled={isDisabled}
                            renderValue={(/** @type {string} */
                                _value,
                            ) => selectedJobTransferType?.label}
                            value={selectedJobTransferType}
                            onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                                event,
                            ) => updateValidationRulesStore('jobTransferValidationType', event.target.value)}
                        >
                            {jobTransferTypeOptions.map((x) => (
                                <MenuItem
                                    value={x.value}
                                    key={`SelectItem_${x.label}`}
                                >
                                    {x.label}
                                </MenuItem>
                            ))}
                        </StyledSelect>
                        {
                            validationErrors.jobTransferValidationType ? <span style={{ color: 'red' }}>Please select a validation type</span> : null
                        }
                        <StyledSwitch
                            checked={isSkills}
                            onChange={(value) => updateValidationRulesStore('isSkills', value)}
                            label={'Skills'}
                            disabled={isDisabled}
                        />
                        <StyledSwitch
                            checked={isCertifications}
                            onChange={(value) => updateValidationRulesStore('isCertifications', value)}
                            label={'Certifications'}
                            disabled={isDisabled}
                        /></>)}
                <OnProbationSection {...props} />
                <OnRestrictionSection {...props} />
                <div>
                    <StyledSwitch
                        checked={isEmployeeEligibility}
                        onChange={(value) => updateValidationRulesStore('isEmployeeEligibility', value)}
                        label={'Employee eligibility based on hours worked'}
                        disabled={isDisabled}
                    />
                    {isEmployeeEligibility && (<>
                        <NumberInput
                            handleChangeInput={(value, target) => updateValidationRulesStore(target, value)}
                            min={1}
                            max={999}
                            step={1}
                            precision={0}
                            value={employeeEligibilityLength}
                            defaultValue={1}
                            target={'employeeEligibilityLength'}
                            isDisabled={isDisabled}
                            error={validationErrors.employeeEligibilityLength || (isEmployeeEligibility && employeeEligibilityLength && employeeEligibilityLength < 1)}
                        />
                        <Typography
                            variant="body1"
                            className="ers-numeric-input-unit-label"
                        >
                            Hours during the following period
                        </Typography>
                        <StyledSelect
                            labelId="select-symbolid-period-label"
                            id="select-symbolid-period-type"
                            disabled={isDisabled}
                            renderValue={(/** @type {string} */
                                _value,
                            ) => selectedEmployeeEligibilityPeriod?.name}
                            value={employeeEligibilityPeriod !== null ? selectedEmployeeEligibilityPeriod : null}
                            onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                                event,
                            ) => updateValidationRulesStore('employeeEligibilityPeriod', event.target.value)}
                            style={{ width: '300px', display: 'inline-grid' }}
                        >
                            {symbolicPeriods.map((x) => (
                                <MenuItem
                                    value={x.id}
                                    key={`SelectItem_${x.name}`}
                                >
                                    {x.name}
                                </MenuItem>
                            ))}
                        </StyledSelect>
                    </>)}
                </div>
                {
                    validationErrors.employeeEligibilityLength ? <span style={{ color: 'red' }}>The number of hours must be greater than 0</span> : null
                }
                {
                    validationErrors.employeeEligibilityPeriod ? <span style={{ color: 'red', display: 'flex' }}>Please select a period</span> : null
                }
                {isEmployeeEligibility && (
                    <div className="ers-dataview-info-container">
                        <div className="rse--selection-field-container ers-one-half-space">
                            <InputTitle>Hours calculated with this dataview:</InputTitle>
                            <AutoComplete
                                id="dataview-select"
                                value={employeeEligibilityDataviewId !== null ? dataviews.find((x) => x.id === employeeEligibilityDataviewId) : null}
                                options={dataviews}
                                onChange={(_event, optionSelected) => updateValidationRulesStore('employeeEligibilityDataviewId', optionSelected?.id)}
                                renderInput={(params) => (<TextField
                                    {...params}
                                    variant="outlined"
                                    placeholder="Select a dataview"
                                />)}
                                getOptionLabel={(option) => option.name}
                                includeInputInList
                                disabled={isDisabled}
                                disableClearable
                            />
                        </div>
                        <div className="rse--selection-field-container ers-one-half-space">
                            <InputTitle>Field</InputTitle>
                            <StyledSelect
                                labelId="select-dataview-field-label"
                                id="select-dataview-field-type"
                                disabled={isDisabled}
                                renderValue={(/** @type {string} */
                                    _value,
                                ) => selectedEligibilityDataviewKeyAlias?.label}
                                value={employeeEligibilityDataviewKeyAlias !== null ? selectedEligibilityDataviewKeyAlias : null}
                                onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                                    event,
                                ) => updateValidationRulesStore('employeeEligibilityDataviewKeyAlias', event.target.value)}
                            >
                                {eligibilityDataviewFields.map((x) => (
                                    <MenuItem
                                        value={x.value}
                                        key={`SelectItem_${x.label}`}
                                    >
                                        {x.label}
                                    </MenuItem>
                                ))}
                            </StyledSelect>
                        </div>
                    </div>)}
                {
                    validationErrors.employeeEligibilityDataviewId || validationErrors.employeeEligibilityDataviewKeyAlias ? (
                        <span style={{ color: 'red' }}>Please select a dataview and dataview field</span>) : null
                }
            </CardContent>
        </Card>

    );
}
